import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React, { useRef, useState } from 'react'
import { Toggle } from 'sutra/components/Toggle/Toggle'

import {
  CardActionButton,
  CardRoot,
  FilterChip,
  GroupPrefix,
  RevealOnCardHover,
} from 'components/filter-card/filter-card.styles'
import { type BreakdownSelection } from 'features/charts-v2/components/breakdown-selection-utils'
import { BreakdownSelectionPopover } from 'features/charts-v2/components/BreakdownSelectionPopover'
import { MetricSelectionPopover } from 'features/charts-v2/components/MetricSelectionPopover'
import {
  type BreakdownOption,
  BREAKDOWNS,
  type CustomBreakdown,
  type MetricType,
  type TimeBucketOption,
} from 'features/charts-v2/types'
import {
  getBreakdownGroupLabel,
  getCustomBreakdownGroupLabel,
  getFilterGroupLabel,
} from 'features/charts-v2/utils/filter-sidebar-groups'
import { FilterOption } from 'features/filters/types'

// Props for breakdown mode
interface BreakdownModeProps {
  mode: 'breakdown'
  id: string
  filter: FilterOption
  onRemove: () => void
  onBreakdownChange: (newBreakdown: BreakdownSelection) => void
  currentBreakdown: BreakdownOption
  // Time bucket props (only used when breakdown is "Time")
  selectedTimeBucket?: TimeBucketOption | null
  onTimeBucketChange?: (timeBucket: TimeBucketOption | null) => void
  // Constraint props for breakdowns
  invalidBreakdowns?: BreakdownOption[]
  getInvalidReason?: (breakdown: BreakdownOption) => string | null
  // Brand/Product grouping props
  showBrandGrouping?: boolean
  onBrandGroupingChange?: (enabled: boolean) => void
  // Custom breakdowns
  customBreakdowns?: CustomBreakdown[]
  // Flag indicating this card represents a custom breakdown
  isCustomBreakdown?: boolean
  customBreakdownData?: CustomBreakdown
  // All selected breakdowns (to prevent duplicates)
  allSelectedBreakdowns?: BreakdownSelection[]
  // Label for tooltip on drag handle
  label?: string
  // Card label mode
  cardLabelMode?: 'tooltip' | 'text'
  // Whether to show the group prefix (e.g. "Language > Theme")
  showBreakdownHierarchy: boolean
  // Whether to show the small grey card label above the card content
  showCardLabels?: boolean
}

// Props for metric mode
interface MetricModeProps {
  mode: 'metric'
  id: string
  selectedMetric: MetricType
  metricOptions: Array<{
    field: MetricType
    alias: string
    filter_type: string
    values: []
    enabled?: boolean
  }>
  onEdit: (newMetric: MetricType) => void
  onRemove?: () => void
  // Drag and drop is optional for metrics
  enableDragDrop?: boolean
  // All selected metrics (to disable them in the popover)
  allSelectedMetrics?: MetricType[]
  // Label for tooltip on drag handle
  label?: string
  // Card label mode
  cardLabelMode?: 'tooltip' | 'text'
  // Whether to show the small grey card label above the card content
  showCardLabels?: boolean
}

type SelectableCardProps = BreakdownModeProps | MetricModeProps

// Breakdowns that support grouping
const GROUPABLE_BREAKDOWNS = [
  BREAKDOWNS.BRAND.id,
  BREAKDOWNS.PRODUCT.id,
  BREAKDOWNS.THEME.id,
] as const

export const SelectableCard: React.FC<SelectableCardProps> = (props) => {
  const { mode, id, onRemove, label, showCardLabels = false } = props
  const showBreakdownHierarchy =
    mode === 'breakdown'
      ? (props as BreakdownModeProps).showBreakdownHierarchy
      : false
  // Tracks hover on the text chip solely to gate the "click to change
  // option" tooltip below — the chip's own highlight is CSS `:hover` via
  // `FilterChip` now, not this state.
  const [isTextHovered, setIsTextHovered] = useState(false)
  const [showPopover, setShowPopover] = useState(false)
  const [showMetricPopover, setShowMetricPopover] = useState(false)
  const textRef = useRef<HTMLSpanElement>(null)

  // Determine if drag and drop should be enabled
  const enableDragDrop =
    mode === 'breakdown' ||
    (mode === 'metric' && (props as MetricModeProps).enableDragDrop)

  // Conditionally use sortable hook
  const sortableResult = useSortable({
    id,
    disabled: !enableDragDrop,
  })

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = enableDragDrop
    ? sortableResult
    : {
        attributes: {},
        listeners: {},
        setNodeRef: () => {},
        transform: null,
        transition: undefined,
        isDragging: false,
      }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  // Helper functions to get data based on mode
  const getDisplayData = (): {
    displayName: string
    currentSelection: string
    cardType: 'Breakdown' | 'Metric'
    model: string | null
  } => {
    if (mode === 'breakdown') {
      const breakdownProps = props as BreakdownModeProps

      // Determine the model for this breakdown
      let model: string | null = null
      if (
        breakdownProps.isCustomBreakdown &&
        breakdownProps.customBreakdownData
      ) {
        // Custom breakdown - group by the model on customBreakdownData
        model = getCustomBreakdownGroupLabel(
          breakdownProps.customBreakdownData.model,
        )
      } else {
        // Standard breakdown - group from BREAKDOWNS, else from the filter field
        const breakdown = Object.values(BREAKDOWNS).find(
          (b) => b.id === breakdownProps.currentBreakdown,
        )
        model = breakdown
          ? getBreakdownGroupLabel(breakdown.id)
          : getFilterGroupLabel(
              breakdownProps.filter.field,
              'model' in breakdownProps.filter
                ? breakdownProps.filter.model
                : undefined,
            )
      }

      return {
        displayName: breakdownProps.filter.alias || breakdownProps.filter.field,
        currentSelection: breakdownProps.currentBreakdown,
        cardType: 'Breakdown' as const,
        model,
      }
    } else {
      const metricProps = props as MetricModeProps
      const metric = metricProps.metricOptions.find(
        (m) => m.field === metricProps.selectedMetric,
      )
      return {
        displayName:
          metric?.alias || metric?.field || metricProps.selectedMetric,
        currentSelection: metricProps.selectedMetric,
        cardType: 'Metric' as const,
        model: null,
      }
    }
  }

  const displayData = getDisplayData()

  const handleSelectionChange = (
    selection: BreakdownSelection | MetricType,
  ): void => {
    if (mode === 'breakdown') {
      const breakdownProps = props as BreakdownModeProps
      const newBreakdown = selection as BreakdownSelection
      breakdownProps.onBreakdownChange(newBreakdown)
    } else {
      const metricProps = props as MetricModeProps
      const newMetric = selection as MetricType
      if (newMetric && newMetric !== metricProps.selectedMetric) {
        metricProps.onEdit(newMetric)
      }
    }
    setShowPopover(false)
    setShowMetricPopover(false)
  }

  const handleClosePopover = (): void => {
    setShowPopover(false)
  }

  return (
    <CardRoot
      ref={setNodeRef}
      $disabled={false}
      style={{ ...style, width: '100%' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        {/* Drag Handle - only show if drag and drop is enabled */}
        {enableDragDrop && (
          <div
            {...attributes}
            {...listeners}
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              padding: '2px',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              fontSize: '12px',
            }}
            title="Drag to reorder"
          >
            ⋮⋮
          </div>
        )}

        <div style={{ width: '100%' }}>
          {/* Static text label above the card body, gated by showCardLabels */}
          {showCardLabels && label && (
            <div>
              <span
                style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  display: 'block',
                  fontWeight: 400,
                }}
              >
                {label}
              </span>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <FilterChip
                ref={textRef}
                $open={showPopover || showMetricPopover}
                style={{
                  fontWeight: '500',
                  fontSize: '14px',
                  marginLeft: '-8px',
                  width: '100%',
                  userSelect: 'none',
                }}
                onMouseEnter={() => setIsTextHovered(true)}
                onMouseLeave={() => setIsTextHovered(false)}
                onClick={(e) => {
                  e.stopPropagation()
                  if (mode === 'metric') {
                    setShowMetricPopover(!showMetricPopover)
                  } else {
                    setShowPopover(!showPopover)
                  }
                }}
              >
                {showBreakdownHierarchy && displayData.model ? (
                  <>
                    <GroupPrefix>
                      {displayData.model} {'> '}
                    </GroupPrefix>
                    {displayData.displayName}
                  </>
                ) : (
                  displayData.displayName
                )}
              </FilterChip>
            </div>

            {/* Delete Button - only visible on card hover, via CSS - and if
                onRemove is provided */}
            {onRemove && (
              <RevealOnCardHover>
                <CardActionButton
                  type="button"
                  $variant="danger"
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemove()
                  }}
                  title={`Remove ${displayData.cardType.toLowerCase()}`}
                >
                  ×
                </CardActionButton>
              </RevealOnCardHover>
            )}
          </div>

          {/* Brand/Product/Theme grouping toggle */}
          {mode === 'breakdown' &&
            GROUPABLE_BREAKDOWNS.includes(
              displayData.currentSelection as (typeof GROUPABLE_BREAKDOWNS)[number],
            ) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    color: '#6b7280',
                  }}
                >
                  {displayData.currentSelection === BREAKDOWNS.BRAND.id
                    ? 'Group brands'
                    : displayData.currentSelection === BREAKDOWNS.PRODUCT.id
                      ? 'Group products'
                      : 'Group themes'}
                </span>
                <Toggle
                  size="sm"
                  checked={
                    (props as BreakdownModeProps).showBrandGrouping || false
                  }
                  onChange={(checked) => {
                    const breakdownProps = props as BreakdownModeProps
                    breakdownProps.onBrandGroupingChange?.(checked)
                  }}
                />
              </div>
            )}
        </div>
      </div>

      {/* Tooltip on text hover */}
      {isTextHovered && !showPopover && !showMetricPopover && (
        <div
          style={{
            position: 'absolute',
            left: '-200px',
            top: '0',
            width: '180px',
            backgroundColor: '#333',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            {displayData.displayName}
          </div>
          <div style={{ opacity: 0.9 }}>
            Click to change {displayData.cardType.toLowerCase()} option.
          </div>
        </div>
      )}

      {/* Selection Popover - conditional based on mode */}
      {showPopover && mode === 'breakdown' && (
        <BreakdownSelectionPopover
          onBreakdownSelect={handleSelectionChange}
          onClose={handleClosePopover}
          selectedBreakdown={
            ((props as BreakdownModeProps).isCustomBreakdown
              ? (props as BreakdownModeProps).customBreakdownData
              : (props as BreakdownModeProps)
                  .currentBreakdown) as BreakdownSelection
          }
          invalidBreakdowns={(props as BreakdownModeProps).invalidBreakdowns}
          getInvalidReason={(props as BreakdownModeProps).getInvalidReason}
          customBreakdowns={(props as BreakdownModeProps).customBreakdowns}
          allSelectedBreakdowns={
            (props as BreakdownModeProps).allSelectedBreakdowns
          }
          triggerRef={textRef}
          showBreakdownHierarchy={showBreakdownHierarchy}
        />
      )}

      {/* Metric Selection Popover */}
      {showMetricPopover && mode === 'metric' && (
        <MetricSelectionPopover
          onMetricSelect={(metric) => {
            handleSelectionChange(Array.isArray(metric) ? metric[0]! : metric)
          }}
          onClose={() => setShowMetricPopover(false)}
          selectedMetric={(props as MetricModeProps).selectedMetric}
          metricOptions={(props as MetricModeProps).metricOptions}
          triggerRef={textRef}
        />
      )}
    </CardRoot>
  )
}

// Legacy export for backward compatibility
export const BreakdownCard: React.FC<BreakdownModeProps> = (props) => (
  <SelectableCard {...props} mode="breakdown" />
)
